import { CompanySetting } from './company.interface';
import { UserSettingApiResponse } from './user.interface';

export interface Json {
  [key: string]: string | number | boolean | Json | Json[];
}

export type BasicDataType = 'string' | 'number' | 'numberString' | 'date' | 'currency';

export interface CommonApiResponse<T = any, TType extends string = string> {
  data: T;
  httpCode: number;
  message: string;
  status: string;
  type: TType;
}

export type AppEnumMap = {
  [key in AppEnumKey]: EnumItem[];
};

export type AppEnumKey =
  | 'adverseInfluences'
  | 'appeal'
  | 'appliances'
  | 'appraisalReportDownloads'
  | 'appraisalReportExportArtifact'
  | 'appraisalReportForms'
  | 'architecturalRating'
  | 'architecturalStyle'
  | 'associationFeeFrequency'
  | 'associationYN'
  | 'attachmentType'
  | 'authenticationScheme'
  | 'avmProvider'
  | 'AVMReportForms'
  | 'basementExitType'
  | 'basementRating'
  | 'basementType'
  | 'basementTypeSearch'
  | 'buildUpRangeType'
  | 'buyerFinancing'
  | 'carStorageRating'
  | 'complexityReportDownloads'
  | 'complexityReportExportArtifact'
  | 'compsTableColumns'
  | 'conditionSearch'
  | 'ConditionType'
  | 'cooling'
  | 'dataSource'
  | 'DataSource'
  | 'dataSources'
  | 'desktopAppraisalReportForms'
  | 'desktopReportDownloads'
  | 'evaluationReportDownloads'
  | 'evaluationReportExportArtifact'
  | 'evaluationReportForms'
  | 'exposureTimeRangeType'
  | 'exteriorRepairCustomItem'
  | 'firePlaceYN'
  | 'garageType'
  | 'garageTypeSearch'
  | 'garageYN'
  | 'groupIndicatorChart'
  | 'growthType'
  | 'guestHouseYN'
  | 'heating'
  | 'housingDensity'
  | 'imageLabels'
  | 'imageSubCategory'
  | 'inspectionProvider'
  | 'inspectionType'
  | 'inspectorType'
  | 'IntendedUseType'
  | 'interiorRepairCustomItem'
  | 'leaseholdFeeSimple'
  | 'listingCondition'
  | 'listingSource'
  | 'locationRating'
  | 'locationType'
  | 'lotFeatures'
  | 'marketFields'
  | 'marketingRangeType'
  | 'marketRentTrendType'
  | 'MarketValueType'
  | 'mlsStatus'
  | 'occupantType'
  | 'offMarket'
  | 'orderCreationSource'
  | 'orderIdType'
  | 'orderStatus'
  | 'overallRating'
  | 'parkingAttachmentType'
  | 'PhysicalCharacteristicsSource'
  | 'poolType'
  | 'poolTypeSearch'
  | 'poolYN'
  | 'progressType'
  | 'projectedUse'
  | 'projectMaintenance'
  | 'projectMaintenanceRating'
  | 'propertyLandType'
  | 'propertyReportDownloads'
  | 'propertyRights'
  | 'propertySubType'
  | 'propertySubTypeAbbr'
  | 'propertySubTypeSearch'
  | 'ProximityDirection'
  | 'qualityOfConstruction'
  | 'qualitySearch'
  | 'rejectionReasons'
  | 'rentalSource'
  | 'rentalType'
  | 'RepairItemEnum'
  | 'reportCategory'
  | 'reportDownloads'
  | 'reportType'
  | 'saleType'
  | 'scenarioType'
  | 'searchCriteria'
  | 'singleIndicatorChart'
  | 'solarPowerYN'
  | 'source'
  | 'state'
  | 'statusFilter'
  | 'subdivision'
  | 'subjectMarketingTime'
  | 'subjectSaleStatus'
  | 'supplyType'
  | 'valueAdjustmentLines'
  | 'viewFactor'
  | 'viewFactorSearch'
  | 'viewRating'
  | 'YNType'
  | 'zoningClassification'
  | 'zoningCompliance'
  | 'zoningDescription';

export interface EnumItem<T = string> {
  key: T;
  displayValue: string;
  abbreviateDisplayValue?: string;

  /** @deprecated Use `.key` instead */
  enumValue?: string;

  /** @deprecated Use `.displayValue` instead */
  enumDisplayValue?: string;
}

export interface ApplicationSettingApiResponse {
  applicableSettings: AllApplicableSettings;
}

export interface UserPermittedActionsResponse {
  permittedActions: Permission[];
}

export enum AppPermission {
  ORDER_LIST = 'order_list',
  ORDER_CREATE = 'order_create',
  ALL_ORDERS_VIEW = 'all_orders_view',
  COMPANY_EDIT_SETTINGS = 'company_edit_settings',
  COMPANY_SWITCH = 'company_switch',
  COMPANY_ADD = 'company_add',
}

export enum OrderPermission {
  ORDER_EDIT = 'order_edit',
  ORDER_COPY = 'order_copy',
  ORDER_TRANSFER = 'order_transfer',
  ORDER_VIEW = 'order_view',
  ORDER_DELETE = 'order_delete',
  ORDER_DOWNLOAD = 'order_download',
  ORDER_REFRESH = 'order_refresh',
  ORDER_SIGN = 'order_sign',
  ORDER_DELIVERY = 'order_delivery',
}

export type Permission = AppPermission | OrderPermission;

export interface AllApplicableSettings {
  applicationSettings: ApplicationSetting;
  companySettings: CompanySetting;
  userSettings: UserSettingApiResponse;
}

export interface AllCompanyDetailsResponse {
  companies: { companyKey: string; companyName: string }[];
}

export interface ApplicationSetting {
  billingKeyConfig: BillingKeyConfig[];
}

export interface BillingKeyConfig {
  reportType: string;
  orderValue: string;
  billingKey: string;
  orderJsonPath: string;
}
